import { useEffect } from "react";
import { outreachChannels } from "./config/productCapabilities.js";
import ConceptualProductPreview from "./components/ConceptualProductPreview.jsx";
import "./product.css";

const PRODUCT_TITLE = "Apping Sales OS | From Market Opportunity to Sales Action";
const PRODUCT_DESCRIPTION = "Apping Sales OS helps B2B teams understand unfamiliar markets, focus on relevant companies, identify stakeholders and build private sales intelligence over time.";

const productNav = [
  ["Product", "/product"],
  ["Market Entry", "/market-entry"],
  ["Who It’s For", "/who-its-for"],
  ["Growth", "/growth"],
  ["Insights", "/insights"],
  ["About", "/about"],
];

const marketSignals = ["Market information", "Company information", "Industry information", "Commercial context"];
const stakeholderRoles = ["CEO", "Procurement", "Operations", "Commercial", "Plant Management", "Finance"];
const intelligenceInputs = ["Documents", "Meeting notes", "Transcripts", "CRM records", "Emails", "User-entered updates"];
const nextActions = ["Further research", "Opportunity prioritization", "Identify missing information", "Prepare communication", "Consider possible next actions"];
const publicLoop = ["Market", "Companies", "People", "Outreach", "Responses", "Intelligence", "Next action"];
const comparisons = [
  ["A giant contact database", "A market-to-account sales intelligence workflow"],
  ["A raw scraping tool", "Research shaped around the commercial objective"],
  ["A bulk messaging engine", "Contextual and governed outreach"],
  ["A system that forgets after every campaign", "Sales intelligence that can grow with the account"],
];

function ProductHeader() {
  return (
    <header className="product-header">
      <a className="product-brand" href="/" aria-label="Apping Sales OS homepage">
        <img src="/apping-logo.png" alt="Apping Technology" width="985" height="999" />
      </a>
      <nav aria-label="Primary navigation">
        {productNav.map(([label, href]) => <a className={label === "Product" ? "is-active" : ""} key={label} href={href}>{label}</a>)}
      </nav>
      <div className="product-header-actions">
        <button type="button" aria-label="Language selection. English is active" aria-disabled="true">EN <span aria-hidden="true">| 简</span></button>
        <a className="product-button product-button-small" href="/book-a-walkthrough">Book a walkthrough</a>
      </div>
    </header>
  );
}

function SectionIntro({ label, title, children, className = "" }) {
  return (
    <div className={`product-intro ${className}`.trim()}>
      {label ? <p className="product-eyebrow">{label}</p> : null}
      <h2>{title}</h2>
      {children ? <div className="product-copy">{children}</div> : null}
    </div>
  );
}

function ProductFooter() {
  return (
    <footer className="product-footer">
      <a className="product-footer-brand" href="/">Apping Sales OS</a>
      <nav aria-label="Footer navigation">
        <a href="/">Homepage</a>
        <a href="/market-entry">Market Entry</a>
        <a href="/sales-intelligence">Sales Intelligence</a>
        <a href="/who-its-for">Who It’s For</a>
        <a href="/growth">Apping Growth</a>
        <a href="/insights">Insights</a>
        <a href="/about">About</a>
        <a href="/faq">FAQ</a>
        <a href="/book-a-walkthrough">Book a Walkthrough</a>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/business-contact-data">Business Contact Data</a>
        <a href="/privacy-request">Privacy Request</a>
      </nav>
      <p>Apping Technology</p>
    </footer>
  );
}

export default function ProductPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content") || "";
    document.title = PRODUCT_TITLE;
    description?.setAttribute("content", PRODUCT_DESCRIPTION);
    document.documentElement.dataset.page = "product";
    window.__APPING_PRODUCT_READY__ = true;
    return () => {
      document.title = previousTitle;
      description?.setAttribute("content", previousDescription);
      delete document.documentElement.dataset.page;
      delete window.__APPING_PRODUCT_READY__;
    };
  }, []);

  return (
    <div className="product-page">
      <a className="skip-link" href="#product-main">Skip to content</a>
      <ProductHeader />
      <main id="product-main">
        <section className="product-hero" aria-labelledby="product-hero-title">
          <img className="product-hero-art" src="/product-hero-market-intelligence.jpg" alt="Abstract world market connections resolving into focused commercial intelligence" width="1600" height="1024" fetchPriority="high" />
          <div className="product-hero-shade" aria-hidden="true" />
          <div className="product-container product-hero-inner">
            <div className="product-hero-copy">
              <p className="product-eyebrow">Apping Sales OS</p>
              <h1 id="product-hero-title">From market opportunity to sales action.</h1>
              <p className="product-hero-body">Research new markets, focus on relevant companies and people, and carry each insight into the next sales decision.</p>
              <div className="product-actions">
                <a className="product-button" href="/book-a-walkthrough">Book a walkthrough</a>
                <a className="product-text-link" href="#capabilities">See what Sales OS does</a>
              </div>
            </div>
            <figure className="product-hero-photo">
              <img src="/images/editorial/business-team-review.jpg" srcSet="/images/editorial/business-team-review-800.jpg 800w, /images/editorial/business-team-review.jpg 1600w" sizes="(max-width: 820px) 100vw, 48vw" alt="Four colleagues discussing a business decision around a conference table" width="1600" height="1067" fetchPriority="high" decoding="async" />
              <figcaption>Illustrative business meeting. No Apping customer relationship is implied.</figcaption>
            </figure>
            <p className="product-path" aria-label="Market intelligence to account focus to relevant people to outreach to sales intelligence">
              <span>Market intelligence</span><b aria-hidden="true">→</b><span>Account focus</span><b aria-hidden="true">→</b><span>Relevant people</span><b aria-hidden="true">→</b><span>Outreach</span><b aria-hidden="true">→</b><span>Sales intelligence</span>
            </p>
          </div>
        </section>

        <section className="product-section positioning-section" aria-labelledby="positioning-title">
          <div className="product-container positioning-layout">
            <SectionIntro title="Not another database of names.">
              <p>Sales OS begins with the commercial objective.</p>
              <p>Tell it what you sell, where you want to grow and what your team is trying to achieve.</p>
              <p>Sales OS helps turn that objective into a clearer view of the market, the companies worth investigating and the people relevant to the opportunity.</p>
            </SectionIntro>
            <blockquote>Start with the opportunity. Not the contact list.</blockquote>
            <figure className="product-context-plate" role="img" aria-label="A public conceptual view of market, company, people and intelligence context connecting around one commercial objective">
              <div><span>Market</span><span>Companies</span><span>People</span><span>Intelligence</span></div>
              <strong>Commercial objective</strong>
              <figcaption>Conceptual product context. This graphic explains the relationship between public capability areas, not proprietary methodology.</figcaption>
            </figure>
          </div>
        </section>

        <div id="capabilities">
          <section id="explore" className="product-section explore-section" aria-labelledby="explore-title">
            <div className="product-container capability-split">
              <SectionIntro label="Explore" title="Understand the market before deciding where to focus.">
                <p>Sales OS helps your team research an unfamiliar market around the product, customer profile and commercial objective you are pursuing.</p>
                <p>It brings relevant market information into a clearer view so your team can begin with context rather than assumptions.</p>
                <p className="product-support">Different markets require different information.</p>
              </SectionIntro>
              <div className="market-field" role="img" aria-label="Conceptual market intelligence view combining market, company, industry and commercial context">
                <div className="market-field-core"><span>Commercial objective</span></div>
                {marketSignals.map((signal, index) => <span className={`market-signal market-signal-${index + 1}`} key={signal}>{signal}</span>)}
              </div>
            </div>
          </section>

          <section id="focus" className="product-section focus-section" aria-labelledby="focus-title">
            <div className="product-container">
              <SectionIntro label="Focus" title="See which companies deserve your attention.">
                <p>Sales OS turns a broad market landscape into a more focused view of companies relevant to your commercial objective.</p>
                <p>Instead of treating every company equally, your team can focus its research and sales effort where there is stronger business relevance.</p>
                <p className="product-support">Many companies. A clearer focus.</p>
              </SectionIntro>
              <div className="company-landscape" role="img" aria-label="A broad company landscape narrowing into a smaller set of relevant companies">
                <div className="landscape-field" aria-hidden="true">
                  {Array.from({ length: 24 }, (_, index) => <i className={index === 8 || index === 15 || index === 20 ? "is-relevant" : ""} key={index} />)}
                </div>
                <div className="focus-window"><span>Focused company view</span><strong>Business relevance</strong></div>
              </div>
            </div>
          </section>

          <section id="connect" className="product-section connect-section" aria-labelledby="connect-title">
            <div className="product-container connect-layout">
              <div className="stakeholder-field" role="img" aria-label="Several stakeholder roles connected to one priority account">
                <div className="account-core"><span>Priority account</span></div>
                {stakeholderRoles.map((role, index) => <span className={`stakeholder stakeholder-${index + 1}`} key={role}>{role}</span>)}
              </div>
              <SectionIntro label="Connect" title="Find the people behind the opportunity.">
                <p>Within priority accounts, Sales OS helps identify relevant decision-makers and stakeholders connected to the sales objective.</p>
                <p>For complex B2B sales, that may include several people with different roles in the buying process.</p>
                <p className="product-support">The right people, in the right business context.</p>
              </SectionIntro>
            </div>
          </section>

          <section id="engage" className="product-section engage-section" aria-labelledby="engage-title">
            <div className="product-container engage-layout">
              <SectionIntro label="Engage" title="Move from intelligence to relevant outreach.">
                <p>Once the right accounts and people are identified, Sales OS is designed to help teams prepare and coordinate outreach using the context already gathered around the opportunity.</p>
                <p>The Sales OS execution layer is being introduced to support coordinated outreach across WhatsApp, email and LinkedIn.</p>
              </SectionIntro>
              <div className="channel-board" aria-label="Planned outreach channels">
                {outreachChannels.map((channel) => (
                  <article key={channel.id}>
                    <div><h3>{channel.label}</h3><p>Coordinated outreach</p></div>
                    <span>{channel.status}</span>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>

        <section className="product-section governance-product-section" aria-labelledby="governance-product-title">
          <div className="product-container governance-product-layout">
            <SectionIntro title="Automation with your rules, not without them.">
              <p>Sales teams should be able to define how outreach operates, including the channels, campaign approach, follow-up rules and approval requirements appropriate to their business.</p>
              <p>Human oversight remains part of the operating model for consequential sales actions.</p>
            </SectionIntro>
            <div className="governance-controls" aria-label="Customer outreach controls">
              <span>Channels</span><span>Campaign approach</span><span>Follow-up rules</span><span>Approval requirements</span><strong>Human oversight</strong>
            </div>
          </div>
        </section>

        <section id="learn" className="product-section learn-section" aria-labelledby="learn-title">
          <div className="product-container learn-layout">
            <SectionIntro label="Learn" title="Turn every interaction into better sales context.">
              <p>Market research is only useful if the learning stays with the team.</p>
              <p>Sales OS is designed to carry useful account context forward as new information, responses and interactions emerge.</p>
              <p className="product-support">The next action should start with more context than the last one.</p>
            </SectionIntro>
            <div className="context-carry" aria-hidden="true">
              <span>Market context</span><span>Account context</span><span>Interaction context</span><strong>Better-informed next decision</strong>
            </div>
          </div>
        </section>

        <section id="private-intelligence" className="product-section private-section" aria-labelledby="private-title">
          <div className="product-container private-layout">
            <SectionIntro label="Private sales intelligence" title="Your intelligence remains your own.">
              <p>Sales OS is designed to bring together relevant account information so your team can build a richer understanding of the opportunity over time.</p>
              <p>Sales OS is designed to work with information such as documents, meeting notes, transcripts, CRM records, emails and user-entered updates.</p>
              <p className="privacy-statement">Each customer’s interaction data contributes to that customer’s own private Sales OS intelligence. It is not pooled into another customer’s intelligence layer.</p>
            </SectionIntro>
            <div className="intelligence-sources" aria-label="Examples of information Sales OS is designed to work with">
              {intelligenceInputs.map((item) => <span key={item}>{item}</span>)}
              <strong>Private account intelligence</strong>
            </div>
          </div>
        </section>

        <section id="next-action" className="product-section action-section" aria-labelledby="action-title">
          <div className="product-container action-layout">
            <SectionIntro label="Act" title="Make the next decision better informed.">
              <p>Sales OS helps bring market, account, stakeholder and interaction context together so the team can decide where attention should go next.</p>
              <p className="product-support">AI supports the decision. Your team owns the decision.</p>
            </SectionIntro>
            <div className="action-options" aria-label="Activities Sales OS can support">
              {nextActions.map((action) => <span key={action}>{action}</span>)}
            </div>
          </div>
        </section>

        <section id="product-loop" className="product-section loop-product-section" aria-labelledby="product-loop-title">
          <div className="product-container">
            <SectionIntro title="One connected sales intelligence loop.">
              <p>Sales OS connects stages that are often fragmented across research tools, spreadsheets, communication channels, CRM records and individual memory.</p>
            </SectionIntro>
            <ol className="public-loop" aria-label="Public Sales OS loop">
              {publicLoop.map((item, index) => <li key={item}><span>{item}</span>{index < publicLoop.length - 1 ? <b aria-hidden="true">→</b> : <b aria-hidden="true">↺</b>}</li>)}
            </ol>
          </div>
        </section>

        <section id="what-it-is-not" className="product-section comparison-section" aria-labelledby="comparison-title">
          <div className="product-container">
            <SectionIntro title="Built for context, not volume for volume’s sake." />
            <div className="comparison-grid">
              {comparisons.map(([notThis, instead]) => (
                <article key={notThis}>
                  <div><span>Not</span><p>{notThis}</p></div>
                  <b aria-hidden="true">→</b>
                  <div><span>Instead</span><h3>{instead}</h3></div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="product-section crm-section" aria-labelledby="crm-title">
          <div className="product-container crm-layout">
            <SectionIntro label="Work with the systems you already have" title="Sales OS does not require you to replace your CRM on day one.">
              <p>Sales OS is designed as an intelligence and operating layer around the sales process.</p>
              <p>Your existing CRM can continue managing the records and processes it already handles while Sales OS helps your team understand markets, accounts, people and next actions.</p>
            </SectionIntro>
            <div className="crm-layers" aria-label="Sales OS working alongside an existing CRM">
              <div><span>Sales OS</span><strong>Intelligence and operating context</strong></div>
              <div><span>Your existing CRM</span><strong>Records and established processes</strong></div>
            </div>
          </div>
        </section>

        <section id="product-example" className="product-section product-example-section" aria-labelledby="product-example-title">
          <div className="product-container example-product-layout">
            <SectionIntro label="Conceptual product preview" title="See the flow, not the machinery.">
              <p>Follow an illustrative Sales OS flow from a market-expansion objective to a focused company landscape, relevant stakeholders and possible next actions.</p>
              <a className="product-text-link" href="#example-output">Explore the illustrative flow</a>
            </SectionIntro>
            <div id="example-output"><ConceptualProductPreview /></div>
          </div>
        </section>

        <section id="product-walkthrough" className="product-section walkthrough-section" aria-labelledby="walkthrough-title">
          <div className="product-container walkthrough-inner">
            <h2 id="walkthrough-title">See Sales OS applied to your market.</h2>
            <p>Tell us what you sell, where you want to grow and what your team is trying to achieve.</p>
            <p>We’ll use the walkthrough to show how Sales OS approaches the problem without turning the public website into a technical product manual.</p>
            <a className="product-button" href="/book-a-walkthrough">Book a walkthrough</a>
            <small>Deeper product configuration and workflow detail is covered during the guided walkthrough.</small>
          </div>
        </section>
      </main>
      <ProductFooter />
    </div>
  );
}
